import styled from 'styled-components';

import { useConfig } from '@wecasa/common/src/providers/config-provider';
import type { UniverseReference, WellnessBasketPrices } from '@wecasa/common/src/types/commons';
import type { CommonModalProps } from '@wecasa/common/src/types/components';

import { getBasketPrices, getPrestationCount } from '@wecasa/customer/src/ducks/basket-selectors';

import { translate } from '@wecasa/i18n';

import type { MembershipOptIn } from '@wecasa/membership/src/types/membership';
import { getMembershipQuestions } from '@wecasa/membership/src/utils/membership-paywall';

import Button from '@wecasa/web-ui/src/components/button/button';
import FullHeightCenteredModal from '@wecasa/web-ui/src/components/modal/full-height-centered-modal';

import { useAppSelector } from '../../../store';
import {
  Content,
  MembershipFaq,
  PriceCardsContainer,
  StickyContainer,
  StyledStickyBottomWrapper,
  StyledWhiteSpacerRoundedBorders,
} from '../common';
import SelectableWellnessMemberPriceCard from './selectable-wellness-member-price-card';
import SelectableWellnessStandardPriceCard from './selectable-wellness-standard-price-card';
import WellnessHeader from './wellness-header';

const t = translate('modals.membership_paywall');

const StyledSpacer = styled.div`
  height: 20px;
`;

type WellnessMembershipPaywallProps = {
  isOpen: CommonModalProps['isOpen'];
  universeReference: UniverseReference;
  optInChoice?: MembershipOptIn;
  closePaywall?: () => void;
  onConfirmPaywall: () => void;
  onOptInChoiceSelection: (newOptIn: MembershipOptIn) => void;
};

const WellnessMembershipPaywall = ({
  isOpen,
  universeReference,
  optInChoice,
  closePaywall,
  onConfirmPaywall,
  onOptInChoiceSelection,
}: WellnessMembershipPaywallProps) => {
  const config = useConfig();
  const prices = useAppSelector((state) => getBasketPrices(state, config)) as WellnessBasketPrices;
  const numberOfSelectedPrestations = useAppSelector(getPrestationCount);

  return (
    <FullHeightCenteredModal isOpen={isOpen} onClose={closePaywall}>
      <StickyContainer>
        <Content>
          <WellnessHeader />
          <StyledWhiteSpacerRoundedBorders />
          <PriceCardsContainer>
            <SelectableWellnessMemberPriceCard
              optInChoice={optInChoice}
              onPress={onOptInChoiceSelection}
              prices={prices}
              numberOfSelectedPrestations={numberOfSelectedPrestations}
            />
            <StyledSpacer />
            <SelectableWellnessStandardPriceCard
              optInChoice={optInChoice}
              onPress={onOptInChoiceSelection}
              prices={prices}
              numberOfSelectedPrestations={numberOfSelectedPrestations}
            />
          </PriceCardsContainer>
          <MembershipFaq
            title={t('.faq.title')}
            questions={getMembershipQuestions({
              universeReference,
            })}
          />
        </Content>
        <StyledStickyBottomWrapper>
          <Button size='lg' onPress={onConfirmPaywall} hasFullWidth isDisabled={!optInChoice}>
            {t('base.continue')}
          </Button>
        </StyledStickyBottomWrapper>
      </StickyContainer>
    </FullHeightCenteredModal>
  );
};

export default WellnessMembershipPaywall;
